import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type MouseEvent,
} from "react";
import dayjs, { type Dayjs } from "dayjs";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ClearIcon from "@mui/icons-material/Clear";
import {
  InputAdornment,
  TextField,
  Typography,
  type TextFieldProps,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import Modal from "@/components/modal";
import GenericTimePicker from "@/components/dateTime/genericTimePicker";
import {
  combineGenericTime,
  formatGenericTime,
  formatGenericTimeInput,
  parseGenericTime,
  type GenericTimeValue,
} from "@/components/dateTime/genericTimeUtils";
import styles from "@/components/dateTime/genericTime.module.css";
import useFocusFirstInvalidField from "@/helper/customHooks/useFocusFirstInvalidField";

interface Props {
  value: Dayjs | null;
  onChange: (value: Dayjs | null) => void;
  onClear?: () => void;
  disabled?: boolean;
  clearable?: boolean;
  className?: string;
  textFieldProps?: Omit<TextFieldProps, "value" | "onChange">;
}

export default function GenericTime({
  value,
  onChange,
  onClear,
  disabled = false,
  clearable = true,
  className,
  textFieldProps,
}: Props) {
  const { t } = useTranslation();
  const ignoreNextInputBlurRef = useRef(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [inputValue, setInputValue] = useState(() =>
    formatGenericTime(value),
  );
  const [draftTime, setDraftTime] = useState<GenericTimeValue | null>(null);
  const [timeValid, setTimeValid] = useState(true);
  const [applyError, setApplyError] = useState(false);
  const { registerValidationTarget, focusFirstInvalidField } =
    useFocusFirstInvalidField<Record<string, never>, "time">({
      fieldOrder: [],
      focusableSelector: 'input[aria-invalid="true"]',
    });
  const isDisabled = disabled || Boolean(textFieldProps?.disabled);

  useEffect(() => {
    setInputValue(formatGenericTime(value));
  }, [value]);

  const initializeDraft = useCallback(() => {
    const validValue = dayjs.isDayjs(value) && value.isValid() ? value : null;
    const now = dayjs().second(0).millisecond(0);
    const initialTime = validValue ?? now.minute(Math.floor(now.minute() / 5) * 5);

    setDraftTime({
      hour: initialTime.hour(),
      minute: initialTime.minute(),
    });
    setTimeValid(true);
    setApplyError(false);
  }, [value]);

  const openModal = (event?: MouseEvent<HTMLElement>) => {
    event?.stopPropagation();
    if (isDisabled) {
      return;
    }
    initializeDraft();
    setModalOpen(true);
  };

  const closeAndDiscard = () => {
    setInputValue(formatGenericTime(value));
    setApplyError(false);
    setModalOpen(false);
  };

  const handleClear = (event?: MouseEvent<HTMLElement>) => {
    event?.preventDefault();
    event?.stopPropagation();
    ignoreNextInputBlurRef.current = false;
    if (isDisabled || !clearable) {
      return;
    }
    onChange(null);
    onClear?.();
    setInputValue("");
    setModalOpen(false);
  };

  const handleAdornmentMouseDown = (event: MouseEvent<HTMLElement>) => {
    ignoreNextInputBlurRef.current = true;
    event.preventDefault();
    window.setTimeout(() => {
      ignoreNextInputBlurRef.current = false;
    }, 0);
  };

  const commitManualInput = () => {
    if (!inputValue.trim()) {
      if (clearable && value !== null) {
        onChange(null);
        onClear?.();
      } else if (!clearable) {
        setInputValue(formatGenericTime(value));
      }
      return;
    }

    const parsed = parseGenericTime(inputValue);
    if (!parsed) {
      setInputValue(formatGenericTime(value));
      return;
    }

    const nextValue = combineGenericTime(parsed, value);
    if (!value || !nextValue.isSame(value)) {
      onChange(nextValue);
    }
    setInputValue(formatGenericTime(nextValue));
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(formatGenericTimeInput(event.target.value));
  };

  const handleInputKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      commitManualInput();
    }
  };

  const handleTimeValidityChange = useCallback((isValid: boolean) => {
    setTimeValid(isValid);
    if (isValid) {
      setApplyError(false);
    }
  }, []);

  const handleApply = () => {
    if (!draftTime || !timeValid) {
      setApplyError(true);
      focusFirstInvalidField({}, ["time"]);
      return;
    }

    const nextValue = combineGenericTime(draftTime, value);
    onChange(nextValue);
    setInputValue(formatGenericTime(nextValue));
    setModalOpen(false);
  };

  return (
    <div className={`${styles.triggerField} ${className ?? ""}`.trim()}>
      <TextField
        {...textFieldProps}
        fullWidth={textFieldProps?.fullWidth ?? true}
        disabled={isDisabled}
        value={inputValue}
        placeholder="HH:mm"
        onChange={handleInputChange}
        onClick={textFieldProps?.onClick}
        onBlur={(event) => {
          if (ignoreNextInputBlurRef.current) {
            ignoreNextInputBlurRef.current = false;
            textFieldProps?.onBlur?.(event);
            return;
          }
          commitManualInput();
          textFieldProps?.onBlur?.(event);
        }}
        onKeyDown={(event) => {
          handleInputKeyDown(event);
          textFieldProps?.onKeyDown?.(event);
        }}
        inputProps={{
          ...textFieldProps?.inputProps,
          maxLength: 5,
          inputMode: "numeric",
        }}
        InputProps={{
          ...textFieldProps?.InputProps,
          endAdornment: (
            <InputAdornment position="end">
              {textFieldProps?.InputProps?.endAdornment}
              {clearable && Boolean(inputValue || value) && (
                <GenericButton
                  variant="icon"
                  color="iconBase"
                  aria-label={t("clear")}
                  onClick={handleClear}
                  disabled={isDisabled}
                  onMouseDown={handleAdornmentMouseDown}
                >
                  <ClearIcon />
                </GenericButton>
              )}
              <GenericButton
                variant="icon"
                color="iconBase"
                aria-label={t("clock_time")}
                aria-haspopup="dialog"
                aria-expanded={modalOpen}
                onClick={openModal}
                disabled={isDisabled}
                onMouseDown={handleAdornmentMouseDown}
              >
                <AccessTimeIcon />
              </GenericButton>
            </InputAdornment>
          ),
        }}
      />

      <Modal
        open={modalOpen}
        onClose={closeAndDiscard}
        onSubmit={handleApply}
        title={t("choose_time")}
        cancelButtonText={t("cancel")}
        submitButtonText={t("apply")}
        buttonPlacement="space-between"
        contentClassName={styles.modalContent}
        maxWidth="18rem"
        paperBorderRadius="0.75rem"
      >
        {draftTime && (
          <GenericTimePicker
            value={draftTime}
            onChange={setDraftTime}
            onValidityChange={handleTimeValidityChange}
            presentation="standalone"
            validationTargetRef={registerValidationTarget("time")}
            validationMessage={
              applyError && (
                <Typography color="error" variant="caption" role="alert">
                  {t("field_required")}
                </Typography>
              )
            }
          />
        )}
      </Modal>
    </div>
  );
}
